import React, { useMemo } from 'react';
import { DataTable } from '@/components';
import { fundingTableColumns } from './FundingDataTableConfig';
import { useResponsive } from '@/hooks/common/useResponsive';
import { 
  IFundingListResponse,
  IFundingTableRow,
  IFundingFilterModel,
  IFundingSortModel,
  IFundingPaginationModel,
  IPropertyDetail
} from '@/types/admin/funding';
import { IDataTableResponse } from '@/types/admin/dataTable';
import { fundingToTableRow } from '@/utils/admin/fundingUtils';

interface FundingDataTableProps {
  data: IFundingListResponse; // Processed API response (count, rows)
  rows?: IFundingTableRow[]; // Optional pre-normalized rows (preferred)
  isLoading: boolean;
  // Table controls now come from DataTable context; props optional for overrides
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  filterModel?: IFundingFilterModel;
  onFilterChange?: (filterModel: IFundingFilterModel) => void;
  sortModel?: IFundingSortModel[];
  onSortChange?: (sortModel: IFundingSortModel[]) => void;
  paginationModel?: IFundingPaginationModel;
  onPaginationChange?: (paginationModel: IFundingPaginationModel) => void;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onFundingSelect?: (funding: IFundingTableRow) => void;
  updateCsvData?: (data: IFundingTableRow[]) => void;
  propertyData?: IPropertyDetail[];
  showSearch?: boolean; // Control whether to show search bar
  // Row selection props
  selectedRows?: IFundingTableRow[];
  onRowSelectionChange?: (selectedRows: IFundingTableRow[]) => void;
  // Action callbacks
  onReprocessComplete?: (updatedFunding: IFundingTableRow) => void;
  // Select all callbacks
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
}

export const FundingDataTable: React.FC<FundingDataTableProps> = ({
  data,
  rows,
  isLoading,
  searchQuery,
  onSearchChange,
  filterModel,
  onFilterChange,
  sortModel,
  onSortChange,
  paginationModel,
  onPaginationChange,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  onFundingSelect,
  updateCsvData,
  propertyData = [],
  showSearch = false,
  selectedRows = [],
  onRowSelectionChange,
  onReprocessComplete,
  onSelectAll,
  onDeselectAll,
}) => {
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  
  // Prefer pre-normalized rows from parent; fallback to converting raw rows
  const normalizedData = useMemo(() => {
    if (rows && rows.length >= 0) return rows;
    return data.rows?.map(fundingToTableRow) || [];
  }, [rows, data.rows]);

  // Transform data to match generic DataTable interface
  const tableData: IDataTableResponse<IFundingTableRow> = useMemo(() => ({
    rows: normalizedData,
    count: data.count,
  }), [normalizedData, data.count]);

  // Create filter options from property data (matching admin-web logic)
  const propertyOptions = useMemo(() => {
    return propertyData?.map(property => ({
      label: property.name,
      value: property.name
    })) || [];
  }, [propertyData]);

  const merchantOptions = useMemo(() => {
    const allMerchantIds = propertyData?.flatMap(property => property.merchantIds) || [];
    const uniqueMerchantIds = [...new Set(allMerchantIds)];
    return uniqueMerchantIds.map(merchantId => ({
      label: merchantId,
      value: merchantId
    }));
  }, [propertyData]);

  // Update column filter options dynamically
  const columnsWithOptions = useMemo(() => {
    return fundingTableColumns(onReprocessComplete, { isDesktop }).map(column => {
      if (column.key === 'property') {
        return { ...column, filterOptions: propertyOptions };
      }
      if (column.key === 'merchantId') {
        return { ...column, filterOptions: merchantOptions };
      }
      return column;
    });
  }, [propertyOptions, merchantOptions, onReprocessComplete, isDesktop]);

  const handleRowSelect = (funding: IFundingTableRow, index: number) => {
    if (onRowSelectionChange) {
      // Use shared toggle helper
      const { toggleSelection } = require('@/utils/admin/selection');
      const newSelection = toggleSelection(selectedRows, funding);
      onRowSelectionChange(newSelection);
    } else {
      onFundingSelect?.(funding);
    }
  };

  return (
    <DataTable
      data={tableData}
      columns={columnsWithOptions}
      isLoading={isLoading}
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      searchPlaceholder="Search funding records..."
      showSearch={showSearch}
      showSort={isDesktop ? false : undefined}
      showFilter={true}
      filterModel={filterModel as any}
      onFilterChange={onFilterChange as any}
      sortModel={sortModel as any}
      onSortChange={onSortChange as any}
      paginationModel={paginationModel as any}
      onPaginationChange={onPaginationChange as any}
      hasNextPage={hasNextPage}
      hasPreviousPage={hasPreviousPage}
      onNextPage={onNextPage}
      onPreviousPage={onPreviousPage}
      onRowSelect={handleRowSelect}
      updateCsvData={updateCsvData}
      emptyStateType="funding"
      mobileColumnCount={3}
      selectedRows={selectedRows}
      onRowSelectionChange={onRowSelectionChange}
      enableRowSelection={true}
      onSelectAll={onSelectAll}
      onDeselectAll={onDeselectAll}
    />
  );
};
